import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { _get } from "../../Utils/Helper"; // Ensure _get is correctly implemented
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import { all_routes } from "../../router/all_routes";
import { login_for_school } from "../../../redux/actions/auth";
import { useAppDispatch } from "../../../redux/store";

const SchoolLogin: React.FC = () => {
  const { short_name, school_id } = useParams<{
    short_name: string;
    school_id: string;
  }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const routes = all_routes;

  const [schoolData, setSchoolData] = useState<{
    school_name: string;
    logo: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [isPasswordVisible, setPasswordVisible] = useState(false);

  const _form = {
    username: "",
    password: "",
  };
  const [form, setForm] = useState(_form);

  useEffect(() => {
    if (school_id) {
      const formattedSchoolId = `SCH/${school_id}`;

      // Fetch school data using query parameters
      _get(
        `get-schools?school_id=${formattedSchoolId}`,
        (res) => {
          if (res.success && res.data) {
            setSchoolData({
              school_name: res.data.school_name,
              logo: res.data.badge_url,
            });
          } else {
            toast.error("School data not found");
          }
        },
        (err) => {
          console.error("Error fetching school data:", err);
          toast.error("Failed to fetch school data");
        }
      );
    }
  }, [school_id]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const onSuccess = (data: any) => {
      setLoading(false);
      toast.success("Login successfully!");
      const user = data.user;
      switch (user.role?.toLowerCase()) {
        case "teacher":
          navigate(routes.teacherDashboard);
          break;
        case "admin":
          navigate(routes.adminDashboard);
          break;
        case "parent":
          navigate(routes.parentDashboard);
          break;
        default:
          navigate(routes.login);
          break;
      }
    };

    const onError = (err: any) => {
      console.error("Login error:", err);
      setLoading(false);
      if (err && err.response?.status === 401) {
        toast.warning("Password or username is not correct");
      } else {
        toast.warning(
          "An error occurred. Please check your connection and try again."
        );
      }
    };

    if (!school_id) {
      toast.error("School ID is missing");
      return;
    }

    const formattedSchoolId = `SCH/${school_id}`;

    dispatch(
      login_for_school(
        {
          username: form.username,
          password: form.password,
          school_id: formattedSchoolId,
          history: navigate,
          type: "users",
        },
        onSuccess,
        onError
      )
    );
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible((prevState) => !prevState);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  if (!schoolData) {
    return <Spinner animation="border" />;
  }

  return (
    <div className="container-fuild">
      <div className="w-100 overflow-hidden position-relative flex-wrap d-block vh-100">
        <div className="row">
          <div className="col-lg-12 col-md-12 col-sm-12">
            <div className="row justify-content-center align-items-center vh-100 overflow-auto flex-wrap ">
              <div className="col-md-4 mx-auto p-4">
                <form onSubmit={handleLogin}>
                  <div>
                    <div className="card">
                      <div className="card-body pb-3">
                        <div className="row text-center">
                          <a href="#">
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: "100%",
                                marginBottom: "20px",
                              }}
                            >
                              <img
                                src={schoolData?.logo || "./assets/img/YMA.png"}
                                style={{
                                  width: 200,
                                  height: 200,
                                }}
                                alt={`${schoolData?.school_name} Logo`}
                              />
                            </div>
                          </a>
                        </div>
                        {schoolData && (
                          <div className="login-or">
                            <span className="span-or">
                              {schoolData?.school_name} SignIn
                            </span>
                          </div>
                        )}
                        <div className="mb-3 ">
                          <label className="form-label">Username</label>
                          <div className="input-icon mb-3 position-relative">
                            <span className="input-icon-addon">
                              <i className="ti ti-mail" />
                            </span>
                            <input
                              type="text"
                              className="form-control"
                              name="username"
                              value={form.username}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <label className="form-label">Password</label>
                          <div className="pass-group">
                            <input
                              type={isPasswordVisible ? "text" : "password"}
                              className="pass-input form-control"
                              name="password"
                              value={form.password}
                              onChange={handleChange}
                              required
                            />
                            <span
                              className={`ti toggle-password ${
                                isPasswordVisible ? "ti-eye" : "ti-eye-off"
                              }`}
                              onClick={togglePasswordVisibility}
                            />
                          </div>
                        </div>
                        <div className="form-wrap form-wrap-checkbox">
                          <div className="d-flex align-items-center">
                            <div className="form-check form-check-md mb-0">
                              <input
                                className="form-check-input mt-0"
                                type="checkbox"
                              />
                            </div>
                            <p className="ms-1 mb-0 ">Remember Me</p>
                          </div>
                          <div className="text-end ">
                            <Link
                              to={routes.forgotPassword}
                              className="link-danger"
                            >
                              Forgot Password?
                            </Link>
                          </div>
                        </div>
                      </div>
                      <div className="p-4 pt-0">
                        <div className="mb-3">
                          <button
                            type="submit"
                            className="btn btn-primary w-100"
                            disabled={loading}
                          >
                            {loading ? <Spinner /> : "Sign In"}
                          </button>
                        </div>
                        <div className="text-center">
                          <h6 className="fw-normal text-dark mb-0">
                            Are you a Student?{" "}
                            <Link to={routes.studentLogin} className="hover-a ">
                              {" "}
                              Login As Student
                            </Link>
                          </h6>
                        </div>
                      </div>
                    </div>
                    <div className="mt-5 text-center">
                      <p
                        className="mb-0 "
                        style={{ fontSize: "25px", color: "black" }}
                      >
                        Powered by{" "}
                        <a
                          href="https://elscholar.ng/"
                          target="_blank"
                          style={{ color: "blue" }}
                        >
                          Elite Edu Tech
                        </a>
                      </p>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchoolLogin;
